import { ScreenDimensionsComponent } from '../../shared/components/screen-dimension-calculator/screen-dimension-component';
import { Component, inject, computed, signal, Renderer2, ViewChild, AfterViewInit, OnDestroy, effect, Injector, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HeaderState } from '../../@core/services/header-state.service';
import { GlobalLoaderComponent } from 'src/app/app-component/global-loader-component';
import { ToastContainerComponent } from 'src/app/app-component/toast-container.component';
import { StoragePrefix } from '@core/constant';
import { SignupStepService } from 'src/app/auth/signup/signup-step.service';
import { SignupSteps } from 'src/app/auth/signup/signup-step';
import { StorageService } from 'src/app/utils';
import { LanguageModalComponent } from 'src/app/shared/components/language-modal.component/language-modal.component';
import { LoginStepService } from 'src/app/auth/login-layout/login-layout-step.service';
import { PortalHostDirective } from 'src/app/shared/directives/portal-host-directive';
import { PortalService } from '@core/services/portal.service';
import { AuthService } from '@core/services/auth';
import { Subscription, filter } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-auth-layout',
  imports: [
    RouterOutlet,
    TranslateModule,
    CommonModule,
    GlobalLoaderComponent,
    ToastContainerComponent,
    LanguageModalComponent,
    PortalHostDirective
  ],
  templateUrl: './auth-layout-component.html',
  styleUrls: ['./auth-layout-component.scss'], // Corrected to styleUrls
})
export class AuthLayoutComponent implements AfterViewInit, OnDestroy {
  @ViewChild(PortalHostDirective, { static: false }) portalHost!: PortalHostDirective;
  _storage = inject(StorageService);
  _portal = inject(PortalService);
  private removeResizeListener?: () => void;
  private routerEventsSub?: Subscription;
  constructor(
    private location: Location,
    public translate: TranslateService,
    public router: Router,
    private renderer: Renderer2
  ) { } // Inject TranslateService in the constructor

  headerH = 220; // starting height
  private readonly minH = 180; // clamp range
  private readonly maxH = 360;
  private readonly k = 0.25; // growth rate per px scrolled
  screenWidth = signal(window.innerWidth);
  screenHeight: number = 0;
  public screenDimensionService = inject(ScreenDimensionsComponent);

  // use directly in template

  private header = inject(HeaderState);
  private injector = inject(Injector);
  private auth = inject(AuthService);
  _signupStep = inject(SignupStepService);
  _loginStep = inject(LoginStepService);
  isShowBlur = signal(true);
  private previousSignupStep: number = SignupSteps.signup;

  private termsPoliciesReady = computed(() => {
    const terms = this.auth.termAndConditionReqData.state().data?.safeHtml;
    const privacy = this.auth.privacyPolicyReqData.state().data?.safeHtml;
    return !!terms && !!privacy;
  });

  /** Show intro blur only when entering Terms; hide when policies are loaded or user dismissed. */
  private termsBlurEffect = effect(
    () => {
      const step = this._signupStep.currentStep();
      const ready = this.termsPoliciesReady();

      if (step === SignupSteps.terms) {
        if (ready) {
          this.isShowBlur.set(false);
        } else if (this.previousSignupStep !== SignupSteps.terms) {
          this.isShowBlur.set(true);
        }
      }

      this.previousSignupStep = step;
    },
    { injector: this.injector },
  );

  isLangPopUpVisible = signal(false);
  currentUrl = signal('');
  title = computed(() => this.header.title());
  wave = computed(() => this.header.wave());
  shouldLockAuthScroll = computed(
    () =>
      this.currentUrl().includes('/signup') &&
      this._signupStep.currentStep() === 3 &&
      this.isShowBlur()
  );
  ngOnInit() {
    this.currentUrl.set(this.router.url);
    this.routerEventsSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.currentUrl.set(this.router.url);
      });

    this.removeResizeListener = this.renderer.listen('window', 'resize', this.onResize.bind(this));
    // const screenDimension =
    //   this.screenDimensionService.updateScreenDimensions();
    // 
    // this.screenWidth.set(screenDimension.width);
    // 
    const saved = this._storage.get(StoragePrefix.LANGUAGE) || 'en';
    this.translate.setDefaultLang('en');
    this.translate.use(saved);
    document.dir = saved === 'ar' ? 'rtl' : 'ltr';
    try {
      document.documentElement.lang = saved;
    } catch { }
  }

  ngAfterViewInit() {
    if (this.portalHost) {
      this._portal.registerHost(this.portalHost);
    } else {
      console.warn('Portal host not found');
    }
  }

  ngOnDestroy(): void {
    this.removeResizeListener?.();
    this.routerEventsSub?.unsubscribe();
  }

  onResize(event: Event): void {
    this.screenWidth.set(window.innerWidth);
  }
  goBack() {
    this.location.back(); // Navigate back to the previous page
  }
  switchLang(arg: any) {
    const lang = typeof arg === 'string' ? arg : arg?.target?.value;
    if (!lang) return;
    this.translate.use(lang);
    document.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    this._storage.set(StoragePrefix.LANGUAGE, lang);
  }

  get currentLang(): string {
    const lang = this._storage.get(StoragePrefix.LANGUAGE || 'en');
    return lang
    // return (
    //   this.translate.currentLang || this.translate.getDefaultLang() || 'en'
    // );
  }
  openLanguagePopUp() {
    this.isLangPopUpVisible.set(true)
  }
}
